import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { MoreVertical, Plus } from "lucide-react";
import { DataTable } from "../shared/data-table";
import { Button } from "ui/button";
import AppConstant from "../Globals/AppConstants";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "ui/dropdown-menu";

const inr = (v: any) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(v) || 0);

export interface EmployeeTableProps {
  data: any[];
  loading: boolean;
  onEdit: (item: any) => void;
  onToggleStatus: (item: any) => void;
  onViewSalesHistory: (item: any) => void;
  onAdd?: () => void;
  searchValue?: string;
  onSearch: (query: string) => void;
  pagination: { page: number; total: number; onChange: (page: number) => void };
  getRowClassName?: (row: any) => string | undefined;
}

export function EmployeeTable({
  data,
  loading,
  onEdit,
  onToggleStatus,
  onViewSalesHistory,
  onAdd,
  searchValue,
  onSearch,
  pagination,
  getRowClassName,
}: EmployeeTableProps) {
  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        id: "employeeCode",
        header: "ID",
        cell: ({ row }) => <span className="whitespace-nowrap font-medium">{row.original?.employeeCode || "-"}</span>,
        meta: { width: "110px" },
      },
      {
        id: "name",
        header: "Name",
        cell: ({ row }) => row.original?.name,
        meta: { width: "200px" },
      },
      {
        id: "mobileNumber",
        header: "Mobile Number",
        cell: ({ row }) => row.original?.mobileNumber || "Nil",
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => {
          const isActive = row.original?.isActive === 1;
          return (
            <span
              className={`px-2 py-1 rounded text-xs font-semibold ${
                isActive
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {isActive ? "Active" : "Inactive"}
            </span>
          );
        },
        meta: { width: "120px" },
      },
      {
        id: "currentMonthSales",
        header: "Current Month",
        cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.currentMonthSales)}</span>,
        meta: { width: "130px" },
      },
      {
        id: "previousMonthSales",
        header: "Previous Month",
        cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.previousMonthSales)}</span>,
        meta: { width: "130px" },
      },
      {
        id: "salesHistory",
        header: "Sales History",
        cell: ({ row }) => {
          const item = row.original;
          return (
            <span
              className="cursor-pointer text-primary underline"
              onClick={() => onViewSalesHistory(item)}
            >
              View
            </span>
          );
        },
        meta: { width: "100px" },
      },
      {
        id: "action",
        header: "Action",
        cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="text-center ">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(item)}>
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onToggleStatus(item)}>
                    {item?.isActive === 1 ? "Inactivate" : "Activate"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
        meta: { sticky: "right", width: "25px" },
      },
    ],
    []
  );

  return (
    <DataTable
      columns={columns}
      data={data}
      loading={loading}
      title="Employees"
      searchable
      searchPlaceholder="Search by Name, Contact number, or ID..."
      searchValue={searchValue}
      onSearch={onSearch}
      pagination={pagination}
      getRowClassName={getRowClassName}
      className="px-6"
      headerActions={
        onAdd && (
          <Button onClick={onAdd} className="gap-2" size="sm">
            <Plus size={18} />
            {AppConstant?.add}
          </Button>
        )
      }
    />
  );
}
